import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FullScreenConfirmOverlay from './components/FullScreenConfirmOverlay';
import PageHeader from './components/PageHeader';
import Pagination from './components/Pagination';
import {
  showToastError,
  showToastSuccess,
  showToastWarning,
} from './components/ToastNotifications';
import {
  getInternalUserRolesTotals,
  revokeInternalUserRole,
  saveInternalUserRole,
} from './hooks/useUsers';
import type { SessionUser } from './types';
import { getErrorMessage, getErrorStatus } from './utils/requestErrors';

type InternalUserRoleRow = {
  id: number;
  email?: string;
  name?: string;
  active?: boolean;
  verifiedEmail?: boolean;
  allOrganizationsRoleName?: string;
  roleName?: string;
  roleNameBeforeEdit?: string;
};

type InternalUserAdministrationPageProps = {
  organizationId?: number | string | null;
  currentUser?: SessionUser | null;
};

const ROLE_OPTIONS = ['', 'Internal Admin', 'Internal Standard', 'Internal Viewer', 'Product Owner'];
const PAGE_SIZE = 12;

function normalizeRows(rows: InternalUserRoleRow[]): InternalUserRoleRow[] {
  return rows.map((row) => {
    const roleName = String(row.allOrganizationsRoleName || row.roleName || '').trim();
    return {
      ...row,
      roleName,
      roleNameBeforeEdit: roleName,
    };
  });
}

export default function InternalUserAdministrationPage({
  organizationId,
  currentUser,
}: InternalUserAdministrationPageProps) {
  const navigate = useNavigate();
  const requestIdRef = useRef(0);
  const canWriteInternalUserRoles = currentUser?.internalRoleActions?.includes('WriteInternalUserRoles') === true;

  const [rows, setRows] = useState<InternalUserRoleRow[]>([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [userPendingRoleRevocation, setUserPendingRoleRevocation] = useState<InternalUserRoleRow | null>(null);

  const effectiveSearch = useMemo(() => {
    const trimmedSearch = search.trim();
    return trimmedSearch.length >= 2 ? trimmedSearch : null;
  }, [search]);

  useEffect(() => {
    const requestId = ++requestIdRef.current;

    if (!organizationId) {
      setRows([]);
      setTotal(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    getInternalUserRolesTotals({ organizationId, page, pageSize: PAGE_SIZE, search: effectiveSearch })
      .then((response) => {
        if (requestId !== requestIdRef.current) return;
        setRows(normalizeRows(response.data || []));
        setTotal(Number(response.total || 0));
      })
      .catch((error) => {
        if (requestId !== requestIdRef.current) return;
        showToastError(getErrorMessage(error, 'Internal roles could not be loaded.'));
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      });
  }, [effectiveSearch, organizationId, page]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const updateInternalRole = (userId: number, newRoleName: string) => {
    setRows((prev) => prev.map((user) => (
      user.id === userId
        ? { ...user, roleName: newRoleName || '' }
        : user
    )));
  };

  const persistInternalRole = async (
    user: InternalUserRoleRow,
    confirmedRoleRevocation: boolean,
  ) => {
    if (!canWriteInternalUserRoles) {
      showToastError('You do not have permission to save internal user roles.');
      return;
    }

    const nextRoleName = String(user.roleName || '').trim();
    const previousRoleName = String(user.roleNameBeforeEdit || '').trim();
    const isAttemptingRoleRevocation = !nextRoleName && previousRoleName.length > 0;

    if (!confirmedRoleRevocation && isAttemptingRoleRevocation) {
      setUserPendingRoleRevocation(user);
      return;
    }

    if (!isAttemptingRoleRevocation) {
      if (!nextRoleName) {
        showToastWarning('Please select a role before saving.');
        return;
      }

      if (nextRoleName === previousRoleName) {
        showToastWarning('No changes detected - please select a different role before saving.');
        return;
      }
    }

    try {
      if (isAttemptingRoleRevocation) {
        await revokeInternalUserRole({ userId: user.id, roleName: previousRoleName });
      } else {
        await saveInternalUserRole({ userId: user.id, roleName: nextRoleName });
      }

      showToastSuccess(`Internal user role ${isAttemptingRoleRevocation ? 'revoked' : 'saved'} successfully.`);
      setRows((prev) => prev.map((row) => (
        row.id === user.id
          ? { ...row, roleNameBeforeEdit: row.roleName || '' }
          : row
      )));
    } catch (error) {
      if (getErrorStatus(error) === 403) {
        showToastError('You do not have permission to save internal user roles.');
      } else {
        showToastError(getErrorMessage(error, 'Could not save the internal user role.'));
      }
    }
  };

  return (
    <div className="stack" data-testid="page-internal-user-administration">
      <FullScreenConfirmOverlay
        show={userPendingRoleRevocation !== null}
        title="Revoke Role"
        message={`Are you sure you want to revoke the ${userPendingRoleRevocation?.roleNameBeforeEdit || 'internal'} role from ${userPendingRoleRevocation?.name || 'this user'}?`}
        cancelLabel="Cancel"
        confirmLabel="Yes, revoke"
        busy={false}
        cancelHidden={false}
        onCancel={() => setUserPendingRoleRevocation(null)}
        onConfirm={async () => {
          const pendingUser = userPendingRoleRevocation;
          if (!pendingUser) return;
          await persistInternalRole(pendingUser, true);
          setUserPendingRoleRevocation(null);
        }}
        testId="confirm-revoke-role-overlay"
      />

      <PageHeader
        title="Internal User Administration"
        subtitle={canWriteInternalUserRoles ? undefined : 'Internal (view only)'}
        actions={
          <button
            className="button"
            type="button"
            onClick={() => navigate('/internal')}
            data-testid="internal-user-administration-back"
            style={{ alignSelf: 'flex-start' }}
          >
            Back
          </button>
        }
      />

      <div className="card filter-card" style={{ marginBottom: 12 }}>
        <div className="filter-bar">
          <div className="filter-left">
            <input
              className="input filter-search"
              type="search"
              placeholder="Search by Name or Email"
              aria-label="Search users by name or email"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
              minLength={2}
              data-testid="internal-user-administration-search"
            />
          </div>
        </div>
      </div>

      <div className="card" data-testid="internal-user-administration-table">
        <table className="table" data-testid="internal-user-administration-table-content">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Internal Role</th>
              <th aria-label="actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((user) => (
              <tr key={user.id} data-testid={`internal-user-administration-row-${user.id}`}>
                <td>{user.name || ''}</td>
                <td>{user.email || '-'}</td>
                <td>
                  <div className="field">
                    <select
                      className="select"
                      value={user.roleName || ''}
                      onChange={(event) => updateInternalRole(user.id, event.target.value)}
                      disabled={!canWriteInternalUserRoles}
                      data-testid={`internal-user-administration-role-${user.id}`}
                    >
                      {ROLE_OPTIONS.map((option) => (
                        <option key={option} value={option}>{option || '(None)'}</option>
                      ))}
                    </select>
                  </div>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    className="button"
                    onClick={() => persistInternalRole(user, false)}
                    disabled={!canWriteInternalUserRoles}
                    data-testid={`internal-user-administration-save-${user.id}`}
                  >
                    Save
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr data-testid={isLoading ? 'internal-user-administration-loading' : 'internal-user-administration-empty-results'}>
                <td colSpan={4}>{isLoading ? 'Loading...' : 'No Results'}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        totalPages={totalPages}
        onPrev={() => {
          setPage((prev) => Math.max(prev - 1, 0));
          setUserPendingRoleRevocation(null);
        }}
        onNext={() => {
          setPage((prev) => prev + 1);
          setUserPendingRoleRevocation(null);
        }}
        format="of"
        prevLabel="<"
        nextLabel=">"
        testId="internal-user-administration-pagination"
      />
    </div>
  );
}
